import styled from 'styled-components';
import { ColorPalette } from 'theme/colors';

import BaseButton from 'components/Buttons/Button';
import BaseCheckbox from 'components/Checkbox';
import BaseInput from 'components/Input';

interface ContainerProps {
  center?: boolean;
}

interface HeaderProps {
  black?: boolean;
}

interface ButtonsContainerProps {
  left?: boolean;
  center?: boolean;
}

interface NextButtonProps {
  noMargin?: boolean;
}

interface CenteredImageProps {
  width: number;
  marginTop?: number | string;
  marginBottom?: number | string;
}

export const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  text-align: ${({ center }) => (center ? 'center' : 'left')};
  max-width: 600px;
  margin: 0 auto;

  @media ${({ theme }) => theme.device.mobile} {
    height: 100%;
  }
`;

export const Header = styled.h1<HeaderProps>`
  color: ${ColorPalette.HEX.CharlestonGreen};
  font-size: 30px;
  font-weight: 600;
  line-height: 32px;

  margin: 0 0 32px 0;

  @media ${({ theme }) => theme.device.mobile} {
    color: ${({ black }) => (black ? ColorPalette.HEX.CharlestonGreen : ColorPalette.HEX.OutrageousOrange)};
    font-size: 20px;
    line-height: 28px;

    margin: 0 0 36px 0;
  }
`;

export const Subheader = styled.h2`
  color: ${ColorPalette.HEX.DimGray};
  font-size: 18px;
  font-weight: normal;
  line-height: 24px;

  margin: 0 0 16px 0;

  & > strong {
    font-weight: 600;
  }

  & > em {
    color: ${ColorPalette.HEX.CharlestonGreen};
  }

  @media ${({ theme }) => theme.device.mobile} {
    font-size: 16px;
    line-height: 24px;

    margin: 0 0 8px 0;
  }
`;

export const Detail = styled.h3`
  color: ${ColorPalette.HEX.Manatee};
  font-size: 16px;
  font-weight: normal;
  line-height: 24px;

  margin: 0 0 30px 0;
`;

export const FieldsContainer = styled.div`
  display: grid;
  grid-template-rows: 1fr;
  grid-gap: 24px;

  @media ${({ theme }) => theme.device.desktop} {
    grid-gap: 12px;
  }
`;

export const Input = styled(BaseInput)`
  @media ${({ theme }) => theme.device.desktop} {
    * > input {
      font-size: 18px;
      height: 60px;
    }
  }
`;

export const Checkbox = styled(BaseCheckbox)`
  display: flex;
  align-items: flex-start;
  margin-top: 16px;
`;

export const TermsContainer = styled.div`
  margin-top: auto;
  margin-bottom: 2px;
  padding-top: 32px;
  color: ${ColorPalette.HEX.Manatee};
`;

export const TermsTitle = styled.span`
  font-size: 14px;
  line-height: 1.43;
  color: ${ColorPalette.HEX.Manatee};
`;

export const TermsText = styled.span`
  font-size: 16px;
  line-height: 24px;
  margin-left: 12px;
  color: ${ColorPalette.HEX.DimGray};

  @media ${({ theme }) => theme.device.mobile} {
    color: ${ColorPalette.HEX.Manatee};
  }

  & > a {
    color: ${({ theme }) => theme.primaryBackgroundColor};
    text-decoration: none;
    cursor: pointer;

    @media ${({ theme }) => theme.device.mobile} {
      color: ${ColorPalette.HEX.CharlestonGreen};
    }
  }
`;

export const ButtonsContainer = styled.div<ButtonsContainerProps>`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-self: ${({ left, center }) => {
    if (left) return 'flex-start';
    if (center) return 'center';
    return 'flex-end';
  }};
  align-items: flex-end;
  flex-wrap: wrap;
  margin-top: 32px;

  @media ${({ theme }) => theme.device.mobile} {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    margin: 0;
    background: ${ColorPalette.HEX.White};
    padding: 16px 16px 24px 16px;
    margin-top: 32px;
  }

  @media ${({ theme }) => theme.device.desktop} {
    & > button {
      ${({ center }) => (center ? 'margin: 0 auto' : '')};
    }
  }
`;

export const ButtonLine = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

export const NextButton = styled(BaseButton)<NextButtonProps>`
  font-size: 16px;
  font-weight: 600;
  height: 50px;
  margin-left: ${({ noMargin }) => (noMargin ? 0 : '56px')};
  margin-top: ${({ noMargin }) => (noMargin ? 0 : '16px')};

  @media ${({ theme }) => theme.device.desktop} {
    min-width: 260px;
    width: fit-content;
  }

  @media ${({ theme }) => theme.device.mobile} {
    height: 56px;
    font-size: 16px;
    width: 100%;
    margin-left: 0;
  }
`;

export const ResendButton = styled(BaseButton)`
  width: fit-content;
  text-align: right;
  font-weight: normal;
  font-size: 16px;
  height: 40px;

  background: none;
  color: ${({ theme }) => theme.primaryBackgroundColor};

  align-self: flex-end;

  @media ${({ theme }) => theme.device.mobile} {
    height: 56px;
    font-size: 16px;
    width: 100%;
    margin-left: 0;
    text-align: center;
  }
`;

export const BackButton = styled.button`
  height: 60px;

  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.primaryBackgroundColor};
  background: none;
  cursor: pointer;

  margin: auto 0;

  @media ${({ theme }) => theme.device.mobile} {
    display: none;
  }
`;

export const CenteredImage = styled.img<CenteredImageProps>`
  width: ${({ width }) => width}px;
  margin-top: ${({ marginTop }) => {
    if (typeof marginTop === 'number') return `${marginTop}px`;
    return marginTop || 0;
  }};
  margin-bottom: ${({ marginBottom }) => {
    if (typeof marginBottom === 'number') return `${marginBottom}px`;
    return marginBottom || 0;
  }};
  align-self: center;
`;

export const BottomSheetTitle = styled.span`
  color: ${ColorPalette.HEX.CharlestonGreen};
  font-weight: 600;
  font-size: 20px;
  line-height: 28px;
  text-align: center;
`;
